import { z } from 'zod';
import { BitemporalQuery, EffectiveAtBody } from './bitemporal.js';
import { ListResponse } from './envelope.js';
/**
 * BusinessFunction — canonical resource shape returned by the
 * /v1/orgs/{slug}/business_functions endpoints and the matching MCP
 * tools. The public id is the prefixed form (`bizfn_<slug>`); see
 * `./ids.ts` for the prefix codec.
 *
 * Fields follow the sfcompute API guidelines:
 *   - `object` literal for type discrimination on the wire
 *   - snake_case fields
 *   - unix epoch seconds for *_at timestamps
 *
 * The legacy `SnapshotDocument.functions: BusinessFunction[]` continues
 * to surface this same shape (BlobSnapshotRepository enriches the
 * snapshot_files JSON rows with the object literal + timestamps so the
 * SPA's `useSnapshot()` keeps working through the transition).
 */
export const BusinessFunction = z.object({
    object: z.literal('business_function'),
    id: z.string().min(1),
    display: z.string().min(1).max(200),
    subtitle: z.string().max(2000).optional(),
    /**
     * Parent business function in the sub-grouping hierarchy, as a prefixed
     * public id (`bizfn_<slug>`) — same id form as `id`. Omitted for a
     * top-level function (no parent). Lets the platform map roll child
     * functions up under a parent for an at-a-glance, tiered view.
     */
    parent_function_id: z.string().min(1).optional(),
    /**
     * The GROUP this function was filed under by the system, as a prefixed public
     * id — the `head` that represents it on the matrix. Omitted when it is not a
     * member of one.
     *
     * SEPARATE FROM `parent_function_id`, which is the hierarchy a PERSON built.
     * The two are different authorities over the same shape, and collapsing them
     * is what the `member` relation exists to stop: a reader that cannot tell them
     * apart cannot tell "I filed this here" from "the system grouped this".
     *
     * Read-only. The user does not set grouping — they express intent by creating
     * a parent, which writes `parent_function_id`.
     */
    head_id: z.string().min(1).optional(),
    /**
     * What kind of function this is, which is what decides how it is treated:
     *
     *   `singlet`     — stands on its own; a column.
     *   `head`        — a label the system minted over related functions, and a
     *                   real function owning code in its own right.
     *   `member`      — filed under a head; not a column, its footprint rolls up.
     *   `enabler`     — a platform layer; not a column, underpins the ones it
     *                   carries.
     *   `user_parent` — created by a person to organise their view; owns no code.
     */
    kind: z.enum(['singlet', 'head', 'member', 'enabler', 'user_parent']).optional(),
    /**
     * Whether this function renders as a column.
     *
     * PUBLISHED BECAUSE ITS ABSENCE WAS THE BUG. The originating complaint for the
     * promotion/parenthood work was "unpromoted BFs are never flagged, anywhere in
     * the system" — and that was true of this contract too. The list endpoint has
     * never filtered on promotion, so an unpromoted function was always IN the
     * response, with nothing on it to say so. A client could render a demoted
     * platform layer beside a live column and not know the difference.
     */
    promoted: z.boolean().optional(),
    /**
     * Timeline lifecycle state at the composed `at` date. Populated only by
     * the timeline / base composer (absent on the plain REST resource):
     *   - `planned`      — declared but no code yet (renders as an empty column)
     *   - `active`       — code exists at this date
     *   - `retired`      — deletion-based deprecation (Goal 2)
     */
    state: z.enum(['planned', 'active', 'retired']).optional(),
    /**
     * Code first-seen (unix seconds) — when implementation for this BF first
     * appeared in commit history. Absent while purely declared / institutional.
     */
    first_appeared_at: z.number().int().nonnegative().optional(),
    /**
     * Most recent activity (unix seconds) across this BF's code — MAX
     * committed_at over its primary clusters. Composer-only; absent when there
     * is no cluster activity signal.
     */
    last_active_at: z.number().int().nonnegative().optional(),
    created_at: z.number().int().nonnegative(),
    updated_at: z.number().int().nonnegative(),
});
// ---- Request bodies ----
/**
 * POST /v1/orgs/{slug}/business_functions.
 *
 * `id` is the slug ("authoring") — the public prefix is added on the
 * response. We accept the slug rather than the prefixed form to match
 * what `define_function` ops have historically stored in the ledger.
 * (Sending `bizfn_authoring` would result in a doubly-prefixed id.)
 */
export const CreateBusinessFunctionBody = EffectiveAtBody.extend({
    id: z.string().min(1).max(120).regex(/^[a-z0-9][a-z0-9_-]*$/i, 'slug must be alphanumeric / _- and start with a letter or digit'),
    display: z.string().min(1).max(200),
    subtitle: z.string().max(2000).optional(),
});
/**
 * PATCH /v1/orgs/{slug}/business_functions/{id}. All fields optional —
 * unset fields are not touched. At least one mutating field required.
 */
export const UpdateBusinessFunctionBody = EffectiveAtBody.extend({
    display: z.string().min(1).max(200).optional(),
    subtitle: z.string().max(2000).nullable().optional(),
    /**
     * Re-parent (or un-parent) this function in the sub-grouping hierarchy.
     * Same prefixed public id form as `id` (`bizfn_<slug>`); pass `null` to
     * clear the parent and return the function to the top level.
     *
     * Until this existed the ONLY writer of `parent_function_id` was
     * `reconcile_function_parents`, which can only ever ADD a parent to an
     * ungrouped function — so a grouping it produced could not be undone
     * selectively through the API.
     */
    parent_function_id: z.string().min(1).nullable().optional(),
    /**
     * Curate this function: the automated reconcile may not re-decide it.
     *
     * A write from a signed-in user pins implicitly, so this exists for the two
     * cases the actor cannot express — RELEASING a pin (`false`), and the admin
     * surface, which writes as `system` on purpose so its events stay attributable
     * and therefore cannot be told apart from a pipeline by actor alone.
     *
     * Deliberately a flag on the thing being curated rather than a separate
     * "locks" resource: the tiers of a business function differ by their
     * attributes, not by their kind.
     */
    pinned: z.boolean().optional(),
    /**
     * Whether this function is a COLUMN on the matrix (PRD §2.5's promotion tier).
     *
     * The applier has accepted this since promotion shipped; it simply had no route,
     * so the only way to demote a function the automated pass promoted — or to
     * promote a candidate it left out — was a direct database write. That is the one
     * curation verb the API could not express.
     *
     * Demoting does NOT delete: the function keeps its identity, its history and its
     * footprint, and stops being drawn. That is the difference between a surface
     * folded into a neighbour and one that is gone, which `deleted_at` records.
     */
    promoted: z.boolean().optional(),
}).refine((b) => b.display !== undefined ||
    b.subtitle !== undefined ||
    b.parent_function_id !== undefined ||
    // Pinning or releasing is a real edit on its own — curating a grouping the
    // reconcile produced needs no other change to it.
    b.pinned !== undefined ||
    // As is promoting or demoting: deciding a function is not a column is a
    // complete edit, with nothing else about it changed.
    b.promoted !== undefined, {
    message: 'PATCH must set at least one of display, subtitle, parent_function_id, pinned, or promoted',
});
/**
 * DELETE body (rare for DELETE, but `effective_at` lets callers
 * record the removal at a non-now timestamp — needed for bitemporal
 * "this was removed effective X" semantics).
 */
export const DeleteBusinessFunctionBody = EffectiveAtBody;
// ---- Response envelopes ----
export const ListBusinessFunctionsQuery = BitemporalQuery;
export const ListBusinessFunctionsResponse = ListResponse(BusinessFunction);
export const BusinessFunctionResponse = z.object({
    business_function: BusinessFunction,
});
export const DeleteBusinessFunctionResponse = z.object({
    deleted: z.literal(true),
    id: z.string(),
});
//# sourceMappingURL=business-functions.js.map